import React, { useCallback, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore, FactCheck } from '@mui/icons-material';
import { getTrustCommentHeadline } from '../utils/trustCitationUtils';

/**
 * Collapsible trust/source commentary for a generated document: short headline from
 * citation metadata, full template goal and technical ids behind an expand chevron only;
 * clicking the headline area scrolls the matching section into view in the editor.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.comments - Rows from `extractTrustComments()` in `trustCitationUtils`.
 * @param {function(number): void} props.onScrollToSection - Scroll editor to the nth `[data-trust-section]`.
 * @param {string} [props.title="Source comments"] - Column heading.
 * @param {boolean} [props.compact=false] - Tighter padding (e.g. dialog).
 * @returns {React.ReactElement|null} Panel or null when empty.
 */
function TrustSourceCommentsPanel({ comments, onScrollToSection, title = 'Source comments', compact = false }) {
  const [expandedById, setExpandedById] = useState(() => ({}));

  const toggleExpanded = useCallback((id, event) => {
    event.stopPropagation();
    event.preventDefault();
    setExpandedById((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleNavigate = useCallback(
    (sectionIndex) => {
      onScrollToSection(sectionIndex);
    },
    [onScrollToSection]
  );

  if (!comments || comments.length === 0) return null;

  const pad = compact ? 1 : 1.25;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" component="h3">
        {title}
      </Typography>
      {comments.map((comment) => {
        const expanded = !!expandedById[comment.id];
        const headline = getTrustCommentHeadline(comment);
        const isCited = comment.status === 'cited';

        return (
          <Paper
            key={comment.id}
            variant="outlined"
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 'var(--q-radius-xl, 12px)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                p: pad,
              }}
            >
              <IconButton
                size="small"
                aria-expanded={expanded}
                aria-label={expanded ? 'Collapse details' : 'Expand details'}
                onClick={(e) => toggleExpanded(comment.id, e)}
                sx={{
                  mt: -0.25,
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              >
                <ExpandMore fontSize="small" />
              </IconButton>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => handleNavigate(comment.sectionIndex)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNavigate(comment.sectionIndex);
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  py: 0.25,
                  borderRadius: 1,
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                  <FactCheck fontSize="small" color={isCited ? 'success' : 'warning'} sx={{ flexShrink: 0, mt: 0.15 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: expanded ? 'unset' : 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: expanded ? 'visible' : 'hidden',
                    }}
                    title={headline}
                  >
                    {headline}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                  <Chip
                    size="small"
                    color={isCited ? 'success' : 'warning'}
                    label={isCited ? 'Cited' : 'Needs a source'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Confidence {comment.confidence || 'n/a'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Collapse in={expanded}>
              <Box sx={{ px: pad, pb: pad, pl: compact ? 6 : 7, pr: pad }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                  Template section (from document)
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 'min(70vh, 520px)',
                    overflow: 'auto',
                    display: 'block',
                    bgcolor: 'var(--q-bg-subtle)',
                    p: 1,
                    borderRadius: 'var(--q-radius-md, 8px)',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {comment.goalPlain || comment.goal}
                </Typography>
                {comment.sourceLineIds.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mt: 1, mb: 0.5 }}>
                      Source line references
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontFamily: 'var(--q-font-mono)', fontSize: '0.7rem', wordBreak: 'break-all' }}>
                      {comment.sourceLineIds.join(', ')}
                    </Typography>
                  </>
                )}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
}

export default TrustSourceCommentsPanel;
